import "leaflet/dist/leaflet.css";

import indigo from "@material-ui/core/colors/indigo";
import Paper from "@material-ui/core/Paper";
import {
  StyledComponentProps,
  StyleRulesCallback,
  Theme,
  withStyles,
  withTheme,
} from "@material-ui/core/styles";
import * as geojson from "geojson";
import * as L from "leaflet";
import * as _ from "lodash";
import * as React from "react";
import { connect } from "react-redux";

import { Dispatch, StoreState } from "../store";
import {
  AreaType,
  MarketShareFilterState,
  MarketShareState,
} from "../types/index";
import MapOverlayHintZoom from "./MapOverlayHintZoom";
import ProgressOverlay from "./ProgressOverlay";

function mapStateToProps({ marketShareFilter, marketShare }: StoreState) {
  return {
    marketShare,
    marketShareFilter,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    changeMarketShareFilter: (payload: any) =>
      dispatch.marketShareFilter.changeMarketShareFilter(payload),
    fetchMarketShare: (marketShareFilter: MarketShareFilterState) =>
      dispatch.marketShare.fetchMarketShare(marketShareFilter),
  };
}
type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

interface State {
  selectedMarketShare: number;
  selectedPostcode: string;
  showPressCtrlToZoomHint: boolean;
  isMounted: boolean;
}

class MarketShareMap extends React.Component<
  Props & { theme: Theme } & StyledComponentProps,
  State
> {
  public state = {
    selectedMarketShare: 0,
    selectedPostcode: "",
    showPressCtrlToZoomHint: false,
    isMounted: false,
  };

  private geojson: L.GeoJSON<any>;

  private map: L.Map;

  private legend = new L.Control({ position: "bottomright" });

  private defaultStylePropsForFeatures = {
    dashArray: "5",
    fillOpacity: 0.7,
    opacity: 1,
    weight: 3,
  };

  private fetchDataDebounced = _.debounce(
    (props: Props) => props.fetchMarketShare(this.props.marketShareFilter),
    500
  );

  private ctrlKeyDown = false;

  private enableMapOverlayHintZoom = _.throttle(() => {
    if (!this.ctrlKeyDown) {
      this.setState({ showPressCtrlToZoomHint: true });
    }
  }, 100);

  private disableMapOverlayHintZoomAfterTimeout = _.debounce(() => {
    this.setState({ showPressCtrlToZoomHint: false });
  }, 1000);

  private setZoomHints = () => {
    const zoomInAnchorElement = document.querySelector<HTMLAnchorElement>(
      ".leaflet-control-zoom-in"
    );
    const zoomOutAnchorElement = document.querySelector<HTMLAnchorElement>(
      ".leaflet-control-zoom-out"
    );
    if (zoomInAnchorElement && zoomOutAnchorElement) {
      zoomInAnchorElement.title = "Karte vergrößern";
      zoomOutAnchorElement.title = "Karte verkleinern";
    }
  };

  public async componentDidMount() {
    this.setState((prev) => ({ ...prev, isMounted: true }));
    this.map = L.map("map", { scrollWheelZoom: false }).setView(
      // Fallback center: Kiel.
      [
        parseFloat(process.env.REACT_APP_MAP_CENTER_LAT || "54.3418925"),
        parseFloat(process.env.REACT_APP_MAP_CENTER_LNG || "10.0554665"),
      ],
      10
    );

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(this.map);

    this.fetchData();
    this.getColorScaleProperties(this.props);

    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keyup", this.handleKeyUp);
  }

  public async componentWillUnmount() {
    this.setState((prev) => ({ ...prev, isMounted: false }));
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("keyup", this.handleKeyUp);
  }

  public async componentDidUpdate(previousProps: Props) {
    if (this.state.isMounted) {
      this.setZoomHints();
      if (
        previousProps.marketShareFilter.areaType !==
        this.props.marketShareFilter.areaType
      ) {
        this.fetchDataDebounced(this.props);
      }

      if (previousProps.marketShare !== this.props.marketShare) {
        const {
          legendArray,
          gradeArray,
          colorArray,
        } = this.getColorScaleProperties(this.props);

        const style = (feature: any) =>
          this.getStyleOptionsForFeature(
            feature.properties.postcode,
            feature.properties.marketShare.marketShare,
            gradeArray,
            colorArray
          );

        if (this.geojson) {
          this.map.removeLayer(this.geojson);
        }
        this.geojson = L.geoJSON(this.props.marketShare.postcodes, {
          onEachFeature: this.onEachFeature,
          style,
        }).addTo(this.map);

        this.createMapLegend(legendArray);
      }
    }
  }

  public render() {
    return (
      <Paper>
        <ProgressOverlay loading={this.props.marketShare.loading}>
          <MapOverlayHintZoom
            onWheel={this.handleOnWheelEvent}
            showPressCtrlToZoomHint={this.state.showPressCtrlToZoomHint}
          >
            <div style={{ height: 600 }} id="map" />
          </MapOverlayHintZoom>
        </ProgressOverlay>
      </Paper>
    );
  }

  private getStyleOptionsForFeature = (
    postcode: string,
    marketShare: number,
    gradeArray: number[],
    colorArray: string[]
  ) => ({
    ...this.defaultStylePropsForFeatures,
    color: this.getBorderColor(postcode),
    fillColor: this.getFillColor(postcode, marketShare, gradeArray, colorArray),
  });

  private getFillColor = (
    postcode: string,
    marketShare: number,
    gradeArray: number[],
    colorArray: string[]
  ) =>
    this.getColor(
      gradeArray,
      colorArray,
      marketShare && marketShare * 100,
      postcode
    );

  private getBorderColor = (postcode: string) => {
    let color = this.props.theme.palette.primary.main;
    if (this.isPostcodeSelected(postcode)) {
      color = this.props.theme.palette.secondary.main;
    } else if (!this.checkIfPostalcodeToDisplay(postcode)) {
      color = "transparent";
    }
    return color;
  };

  private isPostcodeSelected = (postcode: string) =>
    _.includes(
      this.props.marketShareFilter.selectedPostalCodes.map(
        (item) => item.value
      ),
      postcode
    );

  private resetHighlight = (e: any) => {
    this.setState({ selectedMarketShare: 0, selectedPostcode: "" });
    this.geojson.resetStyle(e.target);
  };

  private getColorScaleProperties = (
    props: Props
  ): {
    legendArray: Array<{ label: string; color: string }>;
    gradeArray: number[];
    colorArray: string[];
  } => {
    const maxMarketShare = this.getMinOrMaxMarketShare(
      props.marketShare,
      "max"
    );
    const minMarketShare = this.getMinOrMaxMarketShare(
      props.marketShare,
      "min"
    );
    if (isFinite(maxMarketShare) && isFinite(minMarketShare)) {
      let gradeSpacing: number;
      switch (true) {
        case maxMarketShare < 1:
          gradeSpacing = 0.1;
          break;
        case maxMarketShare < 2:
          gradeSpacing = 0.2;
          break;
        case maxMarketShare < 5:
          gradeSpacing = 0.5;
          break;
        case maxMarketShare < 10:
          gradeSpacing = 1;
          break;
        case maxMarketShare < 20:
          gradeSpacing = 2;
          break;
        case maxMarketShare < 50:
          gradeSpacing = 5;
          break;
        default:
          gradeSpacing = 10;
      }

      const gradeArray = this.getGradeArray(
        minMarketShare,
        maxMarketShare,
        gradeSpacing
      );
      const colorArray = this.getColorArray(gradeArray);

      const diffBetweenGradeRanges = gradeSpacing < 1 ? 0.01 : 0.1;

      const legendArray = this.getLegendArray(
        gradeArray,
        colorArray,
        diffBetweenGradeRanges,
        maxMarketShare
      );
      return { legendArray, gradeArray, colorArray };
    }
    return { legendArray: [], gradeArray: [], colorArray: [] };
  };

  private getLegendArray(
    gradeArray: number[],
    colorArray: string[],
    diffBetweenGradeRanges: number,
    maxMarketShare: number
  ): Array<{ label: string; color: string }> {
    const legendArray: Array<{ label: string; color: string }> = [];
    for (const grade of gradeArray) {
      legendArray.push({
        color: this.getColor(gradeArray, colorArray, grade),
        label: "",
      });
    }
    for (let i = 0; i < gradeArray.length - 1; i++) {
      legendArray[i].label =
        this.toStr(gradeArray[i], diffBetweenGradeRanges) +
        " - " +
        this.toStr(
          gradeArray[i + 1] - diffBetweenGradeRanges,
          diffBetweenGradeRanges,
          true
        );
    }
    legendArray[legendArray.length - 1].label =
      gradeArray.slice(-1)[0] < maxMarketShare
        ? "> " + this.toStr(gradeArray.slice(-1)[0], diffBetweenGradeRanges)
        : this.toStr(gradeArray.slice(-1)[0], diffBetweenGradeRanges);
    return legendArray;
  }

  private getMinOrMaxMarketShare(
    marketShare: MarketShareState,
    maxOrMin: string
  ): number {
    return maxOrMin === "max"
      ? 100 *
          // eslint-disable-next-line prefer-spread
          Math.max.apply(
            Math,
            marketShare.postcodes.features
              .map((f) => f.properties.marketShare.marketShare)
              .filter((m) => m !== undefined)
          )
      : 100 *
          // eslint-disable-next-line prefer-spread
          Math.min.apply(
            Math,
            marketShare.postcodes.features
              .map((f) => f.properties.marketShare.marketShare)
              .filter((m) => m !== undefined)
          );
  }

  private getGradeArray(
    minMarketShare: number,
    maxMarketShare: number,
    gradeSpacing: number
  ): number[] {
    const gradeArray = [0];
    let firstGrade = gradeSpacing;
    if (minMarketShare <= gradeSpacing) {
      gradeArray.push(firstGrade);
    } else {
      firstGrade = 2 * gradeSpacing;
      while (minMarketShare > firstGrade) {
        firstGrade = firstGrade + gradeSpacing;
      }
      gradeArray.push(firstGrade);
    }
    let currentGrade = firstGrade;
    while (currentGrade < maxMarketShare - gradeSpacing) {
      currentGrade = currentGrade + gradeSpacing;
      gradeArray.push(currentGrade);
    }
    return gradeArray;
  }
  private handleOnWheelEvent = () => {
    this.enableMapOverlayHintZoom();
    this.disableMapOverlayHintZoomAfterTimeout();
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.ctrlKey) {
      this.map.scrollWheelZoom.enable();
      this.setState({ showPressCtrlToZoomHint: false });
      this.ctrlKeyDown = true;
    }
  };

  private handleKeyUp = () => {
    this.map.scrollWheelZoom.disable();
    this.ctrlKeyDown = false;
  };

  private onEachFeature = (
    feature: geojson.Feature<geojson.Geometry, any>,
    layer: L.Layer
  ): void => {
    const { marketShare, postcode } = feature.properties;
    this.setLayerOptions(postcode, marketShare.marketShare, layer);
  };

  private setLayerOptions = (
    postcode: string,
    marketShare: number,
    layer: L.Layer
  ) => {
    if (this.checkIfPostalcodeToDisplay(postcode)) {
      layer.bindTooltip(
        `${postcode}, ${this.getMarketShareString(marketShare)}`
      );
      layer.on({
        click: this.handleClick,
        mouseout: this.resetHighlight,
        mouseover: this.highlightFeature,
      });
    }
  };

  private getMarketShareString = (marketShare: number) => {
    if (marketShare === undefined) {
      return "unbekannter Marktanteil";
    } else if (marketShare < 10) {
      return `ca. ${_.ceil(marketShare * 1000) / 10}% Marktanteil`;
    } else {
      return `ca. ${Math.min(_.ceil(marketShare * 100), 85)}% Marktanteil`;
    }
  };

  private toStr(
    i: number,
    diffBetweenGradeRanges: number,
    limit?: true
  ): string {
    const numberOfDecimalPlaces = diffBetweenGradeRanges === 0.01 ? 2 : 1;
    return i.toFixed(limit ? numberOfDecimalPlaces : numberOfDecimalPlaces - 1);
  }

  private createMapLegend(
    legendArray: Array<{ label: string; color: string }>
  ) {
    this.legend.onAdd = () => {
      const { classes } = this.props;
      const div = L.DomUtil.create("div", `${classes && classes.legend}`);
      const title = "Marktanteil in %";
      const grades = legendArray;
      div.innerHTML += `<h4>${title}</h4>`;
      for (const grade of grades.reverse()) {
        div.innerHTML +=
          `<i><img style="background:${
            grade.color
            /* compared to the feature color inside the map the opacity is higher here which is intentional,
            since the map background is darker / more saturated than the white legend background. The aim here
            is a best possible match between the feature colors and the legend colors. */
          };opacity:0.8;width:15px;height:15px;margin-right:3px"></img>` +
          "&nbsp" +
          `<span style="position:relative; bottom:3px">` +
          grade.label +
          "</span></i><br />";
      }
      return div;
    };

    this.legend.addTo(this.map);
  }

  private highlightFeature = (e: L.LeafletEvent): void => {
    const layer = e.target;
    const { marketShare, postcode } = layer.feature.properties;
    this.setState({
      selectedMarketShare: marketShare.marketShare,
      selectedPostcode: postcode,
    });

    layer.setStyle({
      color: _.includes(
        this.props.marketShareFilter.selectedPostalCodes.map(
          (item) => item.value
        ),
        postcode
      )
        ? this.props.theme.palette.secondary.main
        : this.props.theme.palette.primary.main,
      dashArray: "",
    });

    if (!L.Browser.ie && !L.Browser.opera12 && !L.Browser.edge) {
      layer.bringToFront();
    }
  };

  private handleClick = (event: L.LeafletEvent) => {
    const layer = event.target;
    const { postcode } = layer.feature.properties;
    const { selectedPostalCodes } = this.props.marketShareFilter;

    if (
      _.includes(
        selectedPostalCodes.map((item) => item.value),
        postcode
      )
    ) {
      this.props.changeMarketShareFilter({
        selectedPostalCodes: _.reject(
          selectedPostalCodes,
          (item) => item.value === postcode
        ),
      });
    } else {
      this.props.changeMarketShareFilter({
        selectedPostalCodes: [
          ...selectedPostalCodes,
          { label: postcode, value: postcode },
        ],
      });
    }
  };

  private checkIfPostalcodeToDisplay = (postcode: string): boolean => {
    const postCodeNotIAButIAFilter =
      this.props.marketShare.postcodesIA.indexOf(postcode) === -1 &&
      _.isEqual(this.props.marketShareFilter.areaType, [AreaType.IA]);
    const postCodeNotOOAButOOAFilter =
      this.props.marketShare.postcodesIA.indexOf(postcode) !== -1 &&
      _.isEqual(this.props.marketShareFilter.areaType, [AreaType.OOA]);

    return !(postCodeNotIAButIAFilter || postCodeNotOOAButOOAFilter);
  };

  private getColorArray = (gradeArray: number[]): string[] => {
    const color = indigo;
    const colors = [
      color[50],
      color[100],
      color[200],
      color[300],
      color[400],
      color[500],
      color[600],
      color[700],
      color[800],
      color[900],
    ];

    const colorArray = [];
    for (let i = 0; i < gradeArray.length; i++) {
      colorArray.push(
        colors[Math.round((i * (colors.length - 1)) / (gradeArray.length - 1))]
      );
    }
    return colorArray;
  };

  private getColor = (
    gradeArray: number[],
    colorArray: string[],
    marketShare: number | undefined,
    postcode?: string
  ) => {
    if (marketShare === undefined) {
      return "transparent";
    }

    if (postcode && !this.checkIfPostalcodeToDisplay(postcode)) {
      return "transparent";
    }

    for (let i = 1; i < gradeArray.length; i++) {
      if (marketShare < gradeArray[i]) {
        return colorArray[i - 1];
      }
    }
    return colorArray.slice(-1)[0];
  };

  private fetchData = async () => {
    this.props.fetchMarketShare(this.props.marketShareFilter);
  };
}
const styles: StyleRulesCallback<Theme, Props> = (theme: Theme) => ({
  legend: {
    background: "white",
    border: "2px solid black",
    borderRadius: "15px",
    opacity: 1,
    paddingBottom: 10,
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 0,
    textAlign: "left",
    width: "auto",
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(withTheme(MarketShareMap)));



// WEBPACK FOOTER //
// ./src/components/MarketShareMap.tsx